  <template>
      <MainStructure>
          <ChatBot ref="chatBotRef"/>
          <div class="content exercise-view d-flex flex-column flex-grow-1">
              <div class="header-title d-flex align-items-center mb-2">
                  <h4 class="m-0 me-4 exercise-title">{{ exerciseStore.currentExercise.title }}</h4>
                  <BadgeNumberExercise
                      data-tour="exercise-badge"
                      :numberExercise="exerciseStore.currentExercise.order"
                  />
              </div>

              <div class="simulator-block d-flex flex-column align-items-center flex-grow-1 justify-content-center">
                  <div class="simulator-inner d-flex flex-column">
                      <div class="d-flex justify-content-between mb-1" data-tour="nav-buttons">
                           <button @click="navigateExercise('previous')"
                              type="button"
                              class="btn-nav btn btn-primary custom-background-blue d-flex align-items-center justify-content-center"
                              :disabled="exerciseStore.hasPreviousExercise() ? false : true"
                           >
                              <AppIcons
                                  class="me-md-2"
                                  icon="arrow-left"
                                  size="20"
                              />
                              <span class="d-none d-md-inline">Anterior</span>
                          </button>

                           <button @click="navigateExercise('next')"
                              type="button"
                              class="btn-nav btn btn-primary custom-background-blue d-flex align-items-center justify-content-center"
                              :disabled="exerciseStore.hasNextExercise() ? false : true"
                           >
                              <span class="d-none d-md-inline">Próximo</span>
                              <AppIcons
                                  class="ms-md-2"
                                  icon="arrow-right"
                                  size="20"
                              />
                          </button>
                      </div>

                      <iframe
                          ref="exerciseIFrameRef"
                         :src="exerciseStore.currentExercise.url"
                          title="Laboratório de acionamentos elétricos"
                          allowfullscreen="allowfullscreen"
                          allow="xr-spatial-tracking"
                          scrolling="no"
                          class="sizeSimulator"
                          data-tour="simulator"
                      />

                      <div class="d-flex justify-content-end mt-1 fullscreen-btn-wrapper">
                          <button @click="fullScreeenMode"
                              type="button"
                              class="btn-fullscreen btn btn-primary custom-background-blue d-flex align-items-center justify-content-center"
                          >
                              Tela Cheia
                              <AppIcons
                                  class="ms-2"
                                  icon="arrows-fullscreen"
                                  size="20"
                              />
                          </button>
                      </div>
                  </div>
              </div>

              <button @click="openChatBot"
                  type="button"
                  class="tutor-tab btn btn-primary custom-background-blue d-flex align-items-center justify-content-center"
                  :class="{'d-none': isMobileDevice()}"
                  title="Tutor"
                  data-bs-toggle="tooltip"
                  data-bs-placement="left"
                  data-tour="chatbot-button"
              >
                  <AppIcons
                      icon="robot"
                      size="28"
                  />
              </button>

              <button v-if="isMobileDevice()" @click="openChatBot"
                  type="button"
                  class="tutor-mobile btn btn-primary custom-background-blue d-flex align-items-center justify-content-center"
                  title="Tutor"
              >
                  <AppIcons
                      icon="robot"
                      size="20"
                  />
              </button>

              <button v-if="isFullscreen"
                  @click="exitFullscreenMode"
                  type="button"
                  class="exit-fullscreen-btn btn btn-danger d-flex align-items-center justify-content-center"
              >
                  <AppIcons class="me-1" icon="fullscreen-exit" size="18"/>
                  Sair
              </button>
          </div>
      </MainStructure>
  </template>

  <script setup>
      import MainStructure from '@/components/layoutParts/MainStructure.vue';
      import BadgeNumberExercise from '@/components/layoutParts/BadgeNumberExercise.vue';
      import ChatBot from '@/components/layoutParts/ChatBot.vue';
      import AppIcons from '@/components/layoutParts/AppIcons.vue';
      import statusExerciseEnum from '@/enums/statusExerciseEnum';

      import { ref, onMounted, onUnmounted} from 'vue';
      import { useExerciseStore } from '@/stores/exercise';
      import { useLaboratoryStore } from '@/stores/laboratoryStore';
      import { useAuthStore } from '@/stores/auth';
      import { useCsat } from '@/services/csat.service';
      import { useTour } from '@/composables/useTour';
      import ProfileEnum from '@/enums/profileEnum';

      const exerciseStore = useExerciseStore();
      const laboratoryStore = useLaboratoryStore();
      const authStore = useAuthStore();
      const { startTour, registerTourListener } = useTour();

      const TOUR_NAME = 'exercise';

      function getTourSteps() {
          return [
              { element: '[data-tour="simulator"]', popover: { title: 'Simulador', description: 'Aqui você realiza a prática interagindo com o simulador.\nO simulador pode levar alguns segundos para carregar.' } },
              { element: '[data-tour="nav-buttons"]', popover: { title: 'Navegação entre práticas', description: 'Use os botões Próximo ou Anterior para navegar entre exercícios.' } },
              { element: '[data-tour="exercise-badge"]', popover: { title: 'Identificação da prática', description: 'Cada trilha pode conter múltiplos exercícios.' } },
              { element: '[data-tour="chatbot-button"]', popover: { title: 'Chatbot', description: 'Precisa de ajuda? Use o assistente para tirar dúvidas ou receber dicas.' } }
          ];
      }

      registerTourListener(TOUR_NAME, getTourSteps);

      const chatBotRef = ref(null);
      const exerciseIFrameRef = ref(null);
      const isFullscreen = ref(false);
      const { registerActivity } = useCsat();

      let lastLogLength = 0;
      let isProcessingActivity = false;

      onMounted(() => {
          exerciseStore.updateStatusInProgress(exerciseStore.currentExercise.id, exerciseStore.currentExercise.status);

          window.addEventListener('message', handleEventsExercise);
          document.addEventListener('fullscreenchange', onFullscreenChange);

          const profile = authStore.getLoggedInUsernameAndName()?.profile;
          if (profile === ProfileEnum.STUDENT) {
              startTour(TOUR_NAME, getTourSteps());
          }
      });

      onUnmounted(() => {
          window.removeEventListener('message', handleEventsExercise);
          document.removeEventListener('fullscreenchange', onFullscreenChange);
      });

      function navigateExercise(action = 'next') {

          if(action === 'next') {
              exerciseStore.nextExercise();
          } else {
              exerciseStore.previousExercise();
          }

          resetCsatControl();
          chatBotRef.value.closeChatBot();
          chatBotRef.value.cleanChat();

          exerciseStore.updateStatusInProgress(exerciseStore.currentExercise.id, exerciseStore.currentExercise.status);
      }

      function handleEventsExercise(event) {
          const message = event.data;
          if(event.origin !== new URL(exerciseStore.currentExercise.url).origin) { return; }
          switch(message.type) {
              case 'status':
                  if(message.data === 'completed' && exerciseStore.currentExercise.status !== statusExerciseEnum.COMPLETED) {
                      exerciseStore.updateStatusCompleted(exerciseStore.currentExercise.id);
                  }
                  break;
              case 'log':
                  exerciseStore.saveLog(message.data);
                  processCsatLogTrigger(message.data);
                  break;
              case 'fullscreen':
                  if (message.data === 'exit') {
                      exitFullscreenMode();
                  }
                  break;
          }
      }

      function resetCsatControl() {
        lastLogLength = 0;
        isProcessingActivity = false;
      }

      function processCsatLogTrigger(fullLog) {
        const currentLog = fullLog || "";

        if (currentLog.length <= lastLogLength) return;

        const newContent = currentLog.substring(lastLogLength);
        lastLogLength = currentLog.length;

        const isCorrect = newContent.includes("ASSEMBLY CORRECT");
        const isIncorrect = newContent.includes("ASSEMBLY INCORRECT");

        if ((isCorrect || isIncorrect) && !isProcessingActivity) {
          isProcessingActivity = true;

          console.log('CSAT Trigger:', isCorrect ? 'Sucesso' : 'Erro');
          const laboratoryId = laboratoryStore.currentLab?.id;
          if (laboratoryId) {
            registerActivity(laboratoryId);
          }
          setTimeout(() => {
            isProcessingActivity = false;
          }, 2000);
        }
      }

      function openChatBot() {
          chatBotRef.value.openChatBot();
      }

      function fullScreeenMode() {
          const el = exerciseIFrameRef.value;
          if (el.requestFullscreen) {
              el.requestFullscreen();
          } else if (el.webkitRequestFullscreen) {
              el.webkitRequestFullscreen();
          }
      }

      function exitFullscreenMode() {
          if (document.exitFullscreen) {
              document.exitFullscreen();
          } else if (document.webkitExitFullscreen) {
              document.webkitExitFullscreen();
          }
      }

      function onFullscreenChange() {
          isFullscreen.value = !!document.fullscreenElement;
      }

      function isMobileDevice() {
          if(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)){
              return true;
          }

          return false;
      }
  </script>

  <style scoped>
  .content {
      height: 100%;
      max-height: 100%;
      overflow: visible;
  }

  .header-title {
      flex-shrink: 0;
  }

  .simulator-block {
      min-height: 0;
  }

  .simulator-inner {
      width: 65vw;
  }

  .sizeSimulator {
      width: 100%;
      aspect-ratio: 16 / 9;
      border: none;
      overflow: hidden;
  }

  @media (min-width: 1440px) {
      .simulator-inner {
          width: 75vw;
      }

      .sizeSimulator {
          aspect-ratio: 16 / 6.5;
      }
  }

  @media (min-width: 1600px) {
      .sizeSimulator {
          aspect-ratio: 16 / 7.5;
      }
  }

  @media (min-width: 1920px) {
      .simulator-inner {
          width: 60vw;
      }

      .sizeSimulator {
          aspect-ratio: 16 / 8;
      }

      .fullscreen-btn-wrapper {
          display: none !important;
      }
  }

  .btn-nav {
      width: 140px;
  }

  .btn-fullscreen {
      width: 140px;
  }

  .exit-fullscreen-btn {
      position: fixed;
      top: 10px;
      right: 10px;
      z-index: 99999;
  }

  .tutor-tab {
      position: fixed;
      right: 0;
      bottom: 70px;
      padding: 12px 10px;
      border-radius: 8px 0 0 8px;
      z-index: 1040;
  }

  .tutor-mobile {
      position: fixed;
      right: 12px;
      bottom: 70px;
      width: 42px;
      height: 42px;
      border-radius: 50%;
      padding: 0;
      z-index: 1040;
  }

  @media (max-width: 1024px) and (min-width: 769px) {
      .simulator-block {
          justify-content: flex-start !important;
          margin-top: 0.75rem;
      }

      .simulator-inner {
          width: 90vw;
      }

      .btn-nav {
          width: 120px;
          font-size: 0.85rem;
      }

      .btn-fullscreen {
          width: 120px;
          font-size: 0.85rem;
      }

      .sizeSimulator {
          aspect-ratio: 16 / 10;
      }
  }

  @media (max-width: 768px) and (min-width: 768px) {
      .simulator-inner {
          width: 95vw;
      }

      .sizeSimulator {
          aspect-ratio: 16 / 12;
      }
  }

  @media (max-width: 767px) {
      .simulator-block {
          justify-content: flex-start !important;
      }

      .simulator-inner {
          width: calc(100vw - 2rem);
      }

      .exercise-title {
          font-size: 1rem;
      }

      .btn-nav {
          width: auto;
          min-width: 44px;
          padding: 0.5rem 0.75rem;
      }

      .btn-fullscreen {
          width: 100%;
      }

      .sizeSimulator {
          aspect-ratio: 3 / 4;
      }

      .tutor-tab {
          display: none !important;
      }
  }

  </style>

  <style>
  .height-content:has(.exercise-view) {
      overflow: visible !important;
  }
  </style>
